// Vendor + app stylesheets, imported in the same cascade order the old index.html
// declared them (Bootstrap base first, then app overrides). Parcel bundles these
// deterministically into a single CSS bundle.
// Design tokens first: the custom properties become available app-wide, while the legacy
// body/font rules below still win the cascade — so existing pages are untouched.
// Single source of truth, shared with the design mockups (docs/design/spec/01-foundations.md).
import "../docs/design/tokens.css";

import "bootstrap/dist/css/bootstrap.min.css";
import "../assets/font/Roboto/font.css";
import "../assets/css/app.css";
import "../assets/font/HelveticaNeue/fonts.css";
import "../assets/font/font-awesome/css/font-awesome.min.css";
import "../assets/css/style.css";
import "../assets/css/modern-theme.css";

// The few antd appearance rules its theme cannot express. Last, so nothing legacy outranks it —
// though against antd's own later-injected <style> only specificity decides. See the file header.
import "./theme/antd.scss";

// Shared list-page chrome (spec/03 archetype A). Imported ONCE here rather than `@use`d from each
// list page's stylesheet: `@use` re-emits the module into every entry bundle, so a second copy
// landed after the page's own rules and beat them on source order at equal specificity — the
// visits bulk bar's green Approve reverted to the generic inverted button. One copy, and every
// page stylesheet is guaranteed to come after it.
import "./component/list-page.scss";

// Shared detail-page frame (spec/03 archetype B) — head, form+rail grid, section card, dirty-only
// save bar. Same one-import rule and the same reason as list-page.scss above.
import "./component/detail-page.scss";

// Reject → corrective task: the modal and its photo annotator, shared by /photo-review and
// /answers. Extracted from `photo-review.scss` for exactly that reason; same one-import rule.
import "./component/reject-task-modal.scss";

// The photo report review modal (zoom stage, thumbnail strip, facts rail), shared by
// /photo-review and /visit/:id's reports section. Extracted from `photo-review.scss` for the same
// reason as the two above, and under the same one-import rule.
import "./component/review-modal.scss";

// Bootstrap 4 JS (data-toggle behaviours, e.g. the mobile menu in Header.js) needs
// jQuery on window first; ./bootstrap-jquery sets it up before the bundle evaluates.
import "./bootstrap-jquery";
import "bootstrap/dist/js/bootstrap.bundle.min.js";

import React from "react";
import { createRoot } from "react-dom/client";
import { ConfigProvider } from "antd";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { ToastContainer } from "/src/util/alert";

import ErrorBoundary from "./component/ErrorBoundary";
import { saveState } from "./util/redux/localStorage";
import { getStore } from "./util/redux/store";

import AppRouter from "./router";
import { antdTheme } from "./theme/antd";

const store = getStore();
store.subscribe(() => {
  const { me } = store.getState();
  saveState({ me: me });
});

// Last line of defence for async throws that never reach a React boundary
// (rejected promises, event-handler errors). No remote reporting configured — log only.
window.addEventListener("unhandledrejection", event => {
  console.error("Unhandled promise rejection:", event.reason);
});
window.addEventListener("error", event => {
  console.error("Uncaught error:", event.error || event.message);
});

const application = (
  <Provider store={store}>
    <ConfigProvider theme={antdTheme}>
      <div>
        <ToastContainer position="top-center" autoClose={5000} limit={3} />
        <ErrorBoundary>
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
        </ErrorBoundary>
      </div>
    </ConfigProvider>
  </Provider>
);

const root = createRoot(document.getElementById("app"));
root.render(application);
